/**
 * RichTextNotes — lightweight contentEditable notes field (HTML storage).
 * Plain text values from legacy pay apps render unchanged.
 */
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react'

export interface RichTextNotesProps {
  value: string
  onChange: (html: string) => void
  color?: string
  onColorChange?: (color: string) => void
  placeholder?: string
}

const btnStyle: CSSProperties = {
  padding: '4px 8px',
  fontSize: 12,
  border: '1px solid var(--line)',
  background: 'transparent',
  borderRadius: 4,
  marginRight: 4,
  color: 'var(--ink)',
  cursor: 'pointer',
}

export default function RichTextNotes({
  value,
  onChange,
  color = '#1a1a1a',
  onColorChange,
  placeholder = 'Payment terms, ACH info, thank you note...',
}: RichTextNotesProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const savedRange = useRef<Range | null>(null)
  const [swatch, setSwatch] = useState(color)

  function saveSelection() {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange()
    }
  }
  function restoreSelection() {
    const sel = window.getSelection()
    if (savedRange.current && sel) {
      sel.removeAllRanges()
      sel.addRange(savedRange.current)
    }
  }

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    if (document.activeElement === el) return  // never clobber while user is typing
    if (el.innerHTML !== (value || '')) el.innerHTML = value || ''
  }, [value])

  function emitChange() {
    if (editorRef.current) onChange(editorRef.current.innerHTML)
  }

  function execCmd(cmd: string, val?: string) {
    document.execCommand(cmd, false, val)
    editorRef.current?.focus()
    emitChange()
  }

  function handleToolbarMouseDown(e: MouseEvent<HTMLButtonElement>, cmd: string, val?: string) {
    e.preventDefault()
    execCmd(cmd, val)
  }

  function handleColorChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value
    editorRef.current?.focus()
    document.execCommand('foreColor', false, next)
    emitChange()
    onColorChange?.(next)
    setSwatch(next)
  }

  return (
    <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
          padding: '8px 10px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--bg)',
        }}
      >
        <button type="button" title="Bold" style={{ ...btnStyle, fontWeight: 700 }}
          onMouseDown={e => handleToolbarMouseDown(e, 'bold')}>B</button>
        <button type="button" title="Italic" style={{ ...btnStyle, fontStyle: 'italic' }}
          onMouseDown={e => handleToolbarMouseDown(e, 'italic')}>I</button>
        <button type="button" title="Underline" style={{ ...btnStyle, textDecoration: 'underline' }}
          onMouseDown={e => handleToolbarMouseDown(e, 'underline')}>U</button>
        <button type="button" title="Bullet list" style={btnStyle}
          onMouseDown={e => handleToolbarMouseDown(e, 'insertUnorderedList')}>• List</button>
        <label
          title="Text color"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 4, fontSize: 11, color: 'var(--ink3)' }}
          onMouseDown={e => e.preventDefault()}
        >
          Color
          <input type="color" value={swatch} onChange={handleColorChange}
            style={{ width: 28, height: 24, padding: 0, border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer' }} />
        </label>
        <select
          title="Font size"
          defaultValue="3"
          onMouseDown={saveSelection}
          onChange={e => {
            editorRef.current?.focus()
            restoreSelection()
            document.execCommand('fontSize', false, e.target.value)
            emitChange()
          }}
          style={{
            padding: '4px 6px',
            fontSize: 12,
            border: '1px solid var(--line)',
            borderRadius: 4,
            background: 'transparent',
            color: 'var(--ink)',
          }}
        >
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
        </select>
      </div>
      <div
        ref={editorRef}
        className="rtn-editor"
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emitChange}
        style={{
          minHeight: 100,
          padding: '10px 12px',
          fontSize: 13,
          color: 'var(--ink)',
          outline: 'none',
          lineHeight: 1.5,
        }}
      />
    </div>
  )
}
